import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Loader from "../common/Loader";
// import CenterForm from './CenterForm';
import { fetchCenters } from "../../redux/actions/grievanceActions1";
import {
  StatCard,
  ContentCard,
  ActionLink,
  DashboardHeader,
  StatusBadge,
  Icons,
} from "../common/DashboardComponents";
import { useNavigate } from "react-router-dom";
import UpdateCenterList from "../UpdateCenterList/UpdateCenterList";

const CenterList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { centers, loading: centersLoading } = useSelector(
    (state) => state.grievances
  );
  const userData = JSON.parse(localStorage.getItem("user"));
  const [showModal, setShowModal] = useState(false);
  const [editingCenter, setEditingCenter] = useState([]);
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [centersPerPage, setCentersPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchCenters());
  }, [dispatch]);

  useEffect(() => {
    // Filter centers based on search term and status filter
    let result = [...centers];

    if (statusFilter !== "all") {
      result = result.filter(
        (center) =>
          (statusFilter === "active" && center.isActive) ||
          (statusFilter === "inactive" && !center.isActive)
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (center) =>
          (center.name && center.name.toLowerCase().includes(term)) ||
          (center.location && center.location.toLowerCase().includes(term)) ||
          (center.presidentName &&
            center.presidentName.toLowerCase().includes(term)) ||
          (center.secretaryName &&
            center.secretaryName.toLowerCase().includes(term)) ||
          (center.contactNumber && center.contactNumber.includes(term))
      );
    }

    setFilteredCenters(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [centers, searchTerm, statusFilter]);

  // Get current centers for pagination
  const indexOfLastCenter = currentPage * centersPerPage;
  const indexOfFirstCenter = indexOfLastCenter - centersPerPage;
  const currentCenters = filteredCenters.slice(
    indexOfFirstCenter,
    indexOfLastCenter
  );
  const totalPages = Math.ceil(filteredCenters.length / centersPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (centersLoading && centers.length === 0) {
    return <Loader message="Loading centers..." />;
  }

  const totalCenters = centers.length + 1;

  const handleUpdateEvents = (center) => {
    setShowModal(true);
    setEditingCenter(center);
  };

  const handleCloseForm = () => {
    setShowModal(false);
  };

  return (
    <div className="container-fluid">
      <DashboardHeader title="ARESA Centers List" />

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          title="Total Centers"
          value={totalCenters}
          icon={Icons.Centers}
          color="#2196F3"
        />
      </div>

      {/* Filters and Search */}
      <ContentCard title="Filters" icon={Icons.Filter}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search centers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </ContentCard>

      {/* Center Table with Modern Styling */}
      <ContentCard
        title="Center List"
        icon={Icons.Centers}
        // action={<ActionLink text="Add Center" onClick={handleAddCenter} icon={Icons.Add} />}
      >
        {/* Per Page Selector and Count Display */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <label htmlFor="rowsPerPage" className="me-2 text-nowrap">
              Show:
            </label>
            <select
              id="rowsPerPage"
              className="form-select form-select-sm"
              value={centersPerPage}
              onChange={(e) => setCentersPerPage(Number(e.target.value))}
              style={{ width: "auto" }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="ms-2">entries</span>
          </div>
          <div className="text-muted small">
            Showing {filteredCenters.length > 0 ? indexOfFirstCenter + 1 : 0}-
            {Math.min(indexOfLastCenter, filteredCenters.length)} of{" "}
            {filteredCenters.length} centers
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover border">
            <thead className="table-light">
              <tr>
                <th className="fw-semibold py-3">Center ID</th>
                <th className="fw-semibold py-3">Center Address</th>
                <th className="fw-semibold py-3">President</th>
                <th className="fw-semibold py-3">President Rank</th>
                <th className="fw-semibold py-3">President Number</th>
                <th className="fw-semibold py-3">Secretary</th>
                <th className="fw-semibold py-3">Secretary Rank</th>
                <th className="fw-semibold py-3">Secretary Number</th>
                <th className="fw-semibold py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentCenters.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-5">
                    <div className="py-4">
                      <i className="bi bi-search fs-1 text-muted mb-3 d-block"></i>
                      <h5 className="text-muted mb-1">No centers found</h5>
                      <p className="text-muted small">
                        Try adjusting your search criteria
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentCenters.map((center) => (
                  <tr
                    key={
                      center.id ||
                      center.UNIT_CENTRE_ID ||
                      `center-${Math.random()}`
                    }
                    className="align-middle"
                  >
                    <td>{center.name}</td>
                    <td>{center.location}</td>
                    <td>{center.presidentName}</td>
                    <td>{center.presidentRank}</td>
                    <td>{center.presidentMobile1 || center.contactNumber}</td>
                    <td>{center.secretaryName}</td>
                    <td>{center.secretaryRank}</td>
                    <td>
                      {center.secretaryMobile1 || center.secretaryMobile1}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 10 }}>
                        <button
                          onClick={() => {
                            navigate(`/CenterDetails`, {
                              state: {
                                id: center.id || "N/A",
                              },
                            });
                          }}
                          className="btn btn-sm btn-primary rounded-pill px-3"
                        >
                          <i className="bi bi-eye-fill me-1"></i>
                          View
                        </button>
                        {(userData.role !== 4 && userData.role !== 6 && userData.role !== 7) && <button
                          onClick={() => {
                            handleUpdateEvents(center);
                          }}
                          className="btn btn-sm btn-primary rounded-pill px-3"
                        >
                          <i className="bi bi-pencil-fill me-1"></i>
                          Edit
                        </button>}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {filteredCenters.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-4">
            <div>
              <button
                className="btn btn-sm btn-outline-primary me-2"
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-double-left"></i>
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i> Prev
              </button>
            </div>

            <div className="d-flex">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Logic to show 5 pages around current page
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`btn btn-sm mx-1 ${
                      currentPage === pageNum
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={() => paginate(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="mx-1 align-self-center">...</span>
                  <button
                    className="btn btn-sm btn-outline-primary mx-1"
                    onClick={() => paginate(totalPages)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>

            <div>
              <button
                className="btn btn-sm btn-outline-primary me-2"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next <i className="bi bi-chevron-right"></i>
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
              >
                <i className="bi bi-chevron-double-right"></i>
              </button>
            </div>
          </div>
        )}
      </ContentCard>
      {/* 
      {showModal && (
        <UpdateCenterList center={editingCenter} onClose={handleCloseForm} />
      )} */}
      <UpdateCenterList
        onClose={handleCloseForm}
        show={showModal}
        center={editingCenter}
      />
    </div>
  );
};

export default CenterList;
